// File: src/components/Invoices/OperationalAIInsights.jsx

import React from "react";
import {
  Brain,
  CheckCircle,
  AlertTriangle,
  Clock,
  CreditCard,
  FileSearch,
  PackageCheck,
  ShieldCheck,
} from "lucide-react";

const currency = (n) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(Number(n || 0));

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function daysBetween(from, to = new Date()) {
  if (!from) return null;
  const d = new Date(from);
  if (Number.isNaN(d.getTime())) return null;

  return Math.floor((to.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
}

function buildInsights({ invoice, timeline }) {
  const insights = [];

  const status = String(invoice?.status || invoice?.payment_status || "").toLowerCase();
  const amountDue = toNumber(invoice?.amount_due || invoice?.amount);
  const totalPaid = toNumber(invoice?.total_paid);
  const remaining =
    invoice?.balance_due != null
      ? toNumber(invoice.balance_due)
      : Math.max(amountDue - totalPaid, 0);

  const hasPo = Boolean(invoice?.po_id || invoice?.po_number);
  const isPaid = status === "paid" || Boolean(invoice?.paid_at) || remaining <= 0;
  const isApproved = status === "approved" || Boolean(invoice?.approved_at) || isPaid;
  const isRejected = status === "rejected" || Boolean(invoice?.rejected_at);
  const dueDate = invoice?.due_date;
  const overdueDays = dueDate && remaining > 0 ? daysBetween(dueDate) : null;

  if (hasPo) {
    insights.push({
      type: "success",
      icon: PackageCheck,
      title: "PO linkage confirmed",
      message: `Invoice is linked to ${invoice.po_number || `PO #${invoice.po_id}`}, so finance can trace it back to procurement and receiving.`,
    });
  } else {
    insights.push({
      type: "warning",
      icon: FileSearch,
      title: "PO link missing",
      message:
        "Invoice is not linked to a purchase order. Manual review is recommended before approval or payment.",
    });
  }

  if (isRejected) {
    insights.push({
      type: "risk",
      icon: AlertTriangle,
      title: "Payment blocked",
      message:
        "This invoice has been rejected and should not be included in any payment run unless it is reopened.",
    });
  } else if (isPaid) {
    insights.push({
      type: "success",
      icon: ShieldCheck,
      title: "Invoice financially closed",
      message:
        "Invoice has been fully paid. No further finance action is required unless an audit or correction is needed.",
    });
  } else if (isApproved && remaining > 0) {
    insights.push({
      type: "action",
      icon: CreditCard,
      title: "Ready for payment",
      message: `${currency(remaining)} remains outstanding. This invoice can be moved into the next payment batch.`,
    });
  } else {
    insights.push({
      type: "warning",
      icon: Clock,
      title: "Approval still required",
      message:
        "Invoice is not fully approved yet. Finance should complete review before payment is processed.",
    });
  }

  if (overdueDays !== null && overdueDays > 0) {
    insights.push({
      type: "risk",
      icon: AlertTriangle,
      title: "Invoice overdue",
      message: `Invoice is overdue by ${overdueDays} day(s). Supplier relationship and payment terms may be affected.`,
    });
  } else if (dueDate && remaining > 0) {
    insights.push({
      type: "success",
      icon: Clock,
      title: "Due date under control",
      message:
        "Invoice is not overdue based on the current due date. Payment can be scheduled normally.",
    });
  }

  if (amountDue > 0 && totalPaid > 0 && remaining > 0) {
    insights.push({
      type: "warning",
      icon: CreditCard,
      title: "Partial payment detected",
      message:
        "A partial payment has been recorded. Finance should confirm whether the remaining balance is intentional.",
    });
  }

  if (Array.isArray(timeline) && timeline.length > 0) {
    insights.push({
      type: "success",
      icon: CheckCircle,
      title: "Audit trail available",
      message: `${timeline.length} operational event(s) are recorded against this invoice.`,
    });
  } else {
    insights.push({
      type: "warning",
      icon: FileSearch,
      title: "Limited audit visibility",
      message:
        "No detailed timeline events were found. This invoice may need stronger audit logging.",
    });
  }

  return insights;
}

function insightClass(type) {
  const safe = String(type || "").toLowerCase();

  if (safe === "risk") {
    return "border-red-400/30 bg-red-500/10 text-red-300";
  }

  if (safe === "warning") {
    return "border-amber-400/30 bg-amber-500/10 text-amber-300";
  }

  if (safe === "action") {
    return "border-blue-400/30 bg-blue-500/10 text-blue-300";
  }

  return "border-green-400/30 bg-green-500/10 text-green-300";
}

export default function OperationalAIInsights({ invoice, timeline = [] }) {
  const insights = buildInsights({ invoice, timeline });

  const riskCount = insights.filter((x) => x.type === "risk").length;
  const warningCount = insights.filter((x) => x.type === "warning").length;

  return (
    <div className="rounded-2xl border border-white/10 bg-[#101010] p-5">
      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-purple-300" />
            <h2 className="text-lg font-semibold">AI Operational Intelligence</h2>
          </div>
          <p className="mt-1 text-sm text-gray-400">
            Deterministic management insights generated from invoice, PO, payment, and audit data.
          </p>
        </div>

        <div className="flex gap-2 flex-wrap">
          <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-semibold text-gray-300">
            {insights.length} insight(s)
          </span>

          {riskCount > 0 && (
            <span className="rounded-full border border-red-400/30 bg-red-500/10 px-3 py-1 text-xs font-semibold text-red-300">
              {riskCount} risk
            </span>
          )}

          {warningCount > 0 && (
            <span className="rounded-full border border-amber-400/30 bg-amber-500/10 px-3 py-1 text-xs font-semibold text-amber-300">
              {warningCount} warning
            </span>
          )}
        </div>
      </div>

      <div className="space-y-3">
        {insights.map((insight, index) => {
          const Icon = insight.icon || CheckCircle;

          return (
            <div
              key={`${insight.title}-${index}`}
              className="rounded-xl border border-white/10 bg-black/25 p-4"
            >
              <div className="flex items-start gap-3">
                <div
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border ${insightClass(
                    insight.type
                  )}`}
                >
                  <Icon className="h-4 w-4" />
                </div>

                <div>
                  <div className="font-semibold text-white">{insight.title}</div>
                  <div className="mt-1 text-sm text-gray-300">{insight.message}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}